import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Sparkles, Leaf, Loader2, CheckCircle2,
} from 'lucide-react';

// ── Screens definition ──────────────────────────────────────────────
const SCREENS = [
  {
    key: 'welcome',
    eyebrow: 'A short orientation',
    title: 'Before we begin, one shift matters.',
    body: [
      'ReGenflō is not here to make you chase symptoms every day.',
      'It is here to help you understand what your body is showing, why it is responding the way it is, and how your system can gradually return toward coherence.',
    ],
    primary: 'Begin',
    secondary: 'Remind me later',
  },
  {
    key: 'old_model',
    eyebrow: 'Where most of us begin',
    title: 'Most of us were trained to think in symptoms.',
    body: [
      'When something hurts, we ask: how do I remove it?',
      'When energy drops, we ask: what can I take?',
      'When sleep breaks, digestion changes, or discomfort appears, we search for quick relief.',
      'This is natural. But it is incomplete.',
    ],
    promptKey: 'old_model_patterns',
    promptLabel: 'Which patterns do you notice in yourself?',
    promptHint: 'Pick any that feel true. You can change these later.',
    options: [
      'I mostly look for quick relief.',
      'I worry when symptoms change.',
      'I judge progress day by day.',
      'I often feel confused by my body.',
      'I want to understand my body more deeply.',
    ],
    multi: true,
    primary: 'Continue',
  },
  {
    key: 'new_lens',
    eyebrow: 'A new biological lens',
    title: 'Your body is not broken. It is communicating.',
    body: [
      'Fatigue, pain, acidity, bloating, stiffness, anxiety, disturbed sleep, and low energy are not only problems.',
      'They are signals.',
      'They tell us where your system may be overloaded, misaligned, depleted, inflamed, strained, or adapting.',
    ],
    quote: 'From today, we will not only ask: "Did it reduce?"\nWe will ask: "What is my body showing?"',
    primary: 'I understand',
  },
  {
    key: 'body_intelligence',
    eyebrow: 'Living intelligence',
    title: 'Your body is a living intelligence system.',
    body: [
      'Every day your body reads food, sleep, stress, movement, emotion, environment, microbiome signals, and cellular inputs.',
      'It adapts continuously.',
      'ReGenflō works by changing the conditions around your biology so your system can reorganize itself.',
    ],
    promptKey: 'priority_focus',
    promptLabel: 'What feels most important for you right now?',
    promptHint: 'Pick up to two.',
    options: [
      'More stable energy',
      'Calmer digestion',
      'Better sleep rhythm',
      'Less internal stress',
      'Long-term stability',
      'Understanding my patterns',
    ],
    multi: true,
    cap: 2,
    primary: 'Continue',
  },
  {
    key: 'non_linear',
    eyebrow: 'How healing actually moves',
    title: 'Healing is not a straight line.',
    body: [
      'Some days may feel better. Some days may feel confusing.',
      'Your body may stabilize one layer before another. Sleep may shift before energy. Digestion may change before inflammation. Calmness may improve before strength.',
      'A difficult day is not failure. A good day is not completion.',
    ],
    quote: 'The real question is: is your system moving toward coherence?',
    primary: 'Continue',
  },
  {
    key: 'your_role',
    eyebrow: 'Your role in this',
    title: 'You are not passive here.',
    body: [
      'In ReGenflō, you become an active participant in your biological journey.',
      'Your role is to observe honestly, follow daily alignment, respect rhythm, share signals clearly, and allow your system time to adapt.',
    ],
    quote: 'We bring structure. You bring participation. Your biology does the real work.',
    primary: 'Continue',
  },
  {
    key: 'pledge',
    eyebrow: 'My ReGenflō Participant Pledge',
    title: 'A small commitment to yourself.',
    body: [
      'I understand that ReGenflō is not only about reducing symptoms.',
      'I am learning to understand my body as a living biological system.',
      'I will observe my body\u2019s signals with patience and honesty.',
      'I will follow daily instructions as consistently as possible.',
      'I will not judge progress only by one symptom or one day.',
      'I will participate in restoring my body\u2019s coherence.',
    ],
    pledge: true,
    primary: 'Enter CARE OS',
  },
];

export default function PerceptionShift() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [reflections, setReflections] = useState({ old_model_patterns: [], priority_focus: [] });
  const [pledgeChecked, setPledgeChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [remindLimitReached, setRemindLimitReached] = useState(false);

  // ── Load existing status (so user can resume) ─────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/perception-onboarding/status`);
        if (!mounted) return;
        if (r.data?.status === 'completed') {
          // already done — go to dashboard
          navigate('/dashboard', { replace: true });
          return;
        }
        const sr = r.data?.selected_reflections || {};
        setReflections({
          old_model_patterns: sr.old_model_patterns || [],
          priority_focus: sr.priority_focus || [],
        });
        setRemindLimitReached(!!r.data?.remind_limit_reached);
        const cs = r.data?.current_step;
        if (typeof cs === 'number' && cs > 0 && cs < SCREENS.length) {
          setStepIdx(cs);
        }
        // Tell backend we've started (idempotent)
        if (r.data?.status !== 'in_progress') {
          axios.post(`${API}/perception-onboarding/start`).catch(() => {});
        }
      } catch (e) {
        // noop
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  const screen = SCREENS[stepIdx];
  const isLast = stepIdx === SCREENS.length - 1;
  const isFirst = stepIdx === 0;

  const toggleOption = (key, opt, cap) => {
    setReflections(prev => {
      const arr = prev[key] || [];
      if (arr.includes(opt)) return { ...prev, [key]: arr.filter(x => x !== opt) };
      if (cap && arr.length >= cap) return prev;
      return { ...prev, [key]: [...arr, opt] };
    });
  };

  const persistStep = async (nextStepIdx) => {
    try {
      await axios.post(`${API}/perception-onboarding/save-step`, {
        current_step: nextStepIdx,
        selected_reflections: reflections,
      });
    } catch (e) { /* silent */ }
  };

  const goNext = async () => {
    if (busy) return;
    if (isLast) {
      if (!pledgeChecked) {
        toast.error('Please accept the pledge to continue.');
        return;
      }
      setBusy(true);
      try {
        await axios.post(`${API}/perception-onboarding/complete`, {
          pledge_accepted: true,
          current_step: stepIdx,
          selected_reflections: reflections,
        });
        toast.success('Your ReGenflō Shift has begun.');
        navigate('/dashboard', { replace: true });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not complete onboarding.');
      } finally {
        setBusy(false);
      }
      return;
    }
    const next = stepIdx + 1;
    setStepIdx(next);
    persistStep(next);
  };

  const goPrev = () => {
    if (isFirst) return;
    setStepIdx(stepIdx - 1);
  };

  const remindLater = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/perception-onboarding/remind-later`);
      if (res.data?.remind_limit_reached) {
        setRemindLimitReached(true);
        toast.message('You\u2019ve postponed this a few times. Please complete the ReGenfl\u014d Shift before continuing.');
      } else {
        toast.message('We\u2019ll bring this back tomorrow.');
      }
      navigate('/dashboard', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = (typeof detail === 'object' && detail?.error) || null;
      const msg = (typeof detail === 'object' && detail?.message) || null;
      if (code === 'remind_limit_reached') {
        toast.error(msg || 'You\u2019ve postponed this a few times. Please complete the ReGenfl\u014d Shift before continuing.');
        setRemindLimitReached(true);
        // Stay on the page so they can finish — bump them to the first interactive step
        setStepIdx(1);
      } else if (code === 'cannot_remind_when_completed') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const progressPct = useMemo(
    () => Math.round(((stepIdx) / (SCREENS.length - 1)) * 100),
    [stepIdx]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#8C6B55]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F5EF] via-[#F4F2EE] to-[#EFE9DE]" data-testid="perception-shift-page">
      {/* Header strip */}
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[#8C6B55]">
            <Leaf className="h-4 w-4" strokeWidth={1.4} />
            <span className="text-[10px] uppercase tracking-[0.3em] font-['Work_Sans']">The ReGenflō Shift</span>
          </div>
          <span className="text-[10px] text-[#8A8681] font-['Work_Sans']" data-testid="ps-progress">
            {stepIdx + 1} of {SCREENS.length}
          </span>
        </div>
        <div className="h-1 bg-[#E7E2D6] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#C89B7B] to-[#8C6B55] transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        <div className="space-y-5">
          {screen.eyebrow ? (
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans']">
              {screen.eyebrow}
            </p>
          ) : null}
          <h1
            className="text-3xl md:text-4xl font-['Outfit'] font-light text-[#2C2A29] leading-tight"
            data-testid={`ps-title-${screen.key}`}
          >
            {screen.title}
          </h1>
          <div className="space-y-3 max-w-prose">
            {screen.body.map((p, i) => (
              <p key={i} className="text-base text-[#5C5955] font-['Work_Sans'] leading-relaxed">
                {p}
              </p>
            ))}
          </div>

          {screen.quote ? (
            <div className="mt-4 rounded-xl border-l-2 border-[#C89B7B] bg-white/60 p-4">
              <p className="text-base text-[#3B3936] font-['Outfit'] italic leading-relaxed whitespace-pre-line">
                {screen.quote}
              </p>
            </div>
          ) : null}

          {screen.options ? (
            <div className="pt-4">
              <p className="text-sm text-[#3B3936] font-['Work_Sans'] font-medium mb-1">{screen.promptLabel}</p>
              {screen.promptHint ? (
                <p className="text-[11px] text-[#8A8681] mb-3">{screen.promptHint}</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {screen.options.map(opt => {
                  const arr = reflections[screen.promptKey] || [];
                  const selected = arr.includes(opt);
                  const disabled = screen.cap && !selected && arr.length >= screen.cap;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleOption(screen.promptKey, opt, screen.cap)}
                      disabled={disabled}
                      data-testid={`ps-opt-${screen.key}-${opt.replace(/\W+/g, '-').toLowerCase().slice(0, 24)}`}
                      className={[
                        'px-4 py-2 rounded-full text-sm border transition-all font-["Work_Sans"]',
                        selected
                          ? 'bg-[#2C2A29] text-white border-[#2C2A29]'
                          : disabled
                          ? 'bg-white/60 text-[#B5B0A8] border-[#E3DFD7] cursor-not-allowed'
                          : 'bg-white text-[#3B3936] border-[#E3DFD7] hover:border-[#C89B7B]',
                      ].join(' ')}
                    >{opt}</button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {screen.pledge ? (
            <div className="mt-4 rounded-xl bg-white border border-[#E7E2D6] p-4 flex items-start gap-3">
              <Checkbox
                checked={pledgeChecked}
                onCheckedChange={(c) => setPledgeChecked(!!c)}
                data-testid="ps-pledge-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-[#3B3936] font-['Work_Sans']">
                I understand and accept this journey.
              </span>
            </div>
          ) : null}
        </div>

        {/* Footer actions */}
        <div className="mt-10 flex items-center justify-between gap-3">
          {!isFirst ? (
            <Button
              variant="ghost"
              onClick={goPrev}
              className="text-[#5C5955] hover:text-[#2C2A29] font-['Work_Sans']"
              data-testid="ps-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          ) : screen.secondary && !remindLimitReached ? (
            <Button
              variant="ghost"
              onClick={remindLater}
              disabled={busy}
              className="text-[#5C5955] hover:text-[#2C2A29] font-['Work_Sans']"
              data-testid="ps-remind-later-btn"
            >
              {screen.secondary}
            </Button>
          ) : <span />}

          <Button
            onClick={goNext}
            disabled={busy || (isLast && !pledgeChecked)}
            className="bg-[#2C2A29] hover:bg-[#1C1A19] text-white font-['Work_Sans'] rounded-full px-6"
            data-testid={`ps-primary-btn-${screen.key}`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : isLast ? <Sparkles className="h-4 w-4 mr-1" />
                  : null}
            {screen.primary}
            {!isLast ? <ChevronRight className="h-4 w-4 ml-1" /> : null}
          </Button>
        </div>

        {isLast ? (
          <div className="mt-8 rounded-md border border-[#D6E3D8] bg-[#F0F6F1] p-3 text-xs text-[#3F604C] flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              On entering CARE OS, you receive a <strong>Biological Participant</strong> badge on your dashboard.
              Your selections will help your care team meet you where you are.
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
